import MapLibreGL from "maplibre-gl";
import { useCallback, useMemo, useState } from "react";
import { LngLat, ViewStateChangeEvent, useMap } from "react-map-gl/maplibre";

import { ProjectRead } from "@nens/rana/api-v1";
import { convertLngLatBoundsToExtent } from "@nens/react-map-gl/utils";
import { BoundingBox, Extent } from "@nens/shared-utils";

import { useProjectListMapBounds } from "./hooks/use-project-list-map-bounds";
import { MapProject } from "./types/map-project";
import { useTenantProjectListData } from "./use-tenant-project-list-data";

function locationFilterProject(project: ProjectRead, mapExtent: Extent): boolean {
  const location = project.location;
  const boundingBox = new BoundingBox(...mapExtent);
  return location != null && !!boundingBox.containsPoint(location);
}

export function useTenantProjectList(mapId: string) {
  const { [mapId]: map } = useMap();
  const projectList = useTenantProjectListData();
  const [hoveredProjectId, setHoveredProjectId] = useState<string | null>(null);
  const [popup, setPopup] = useState<null | {
    point: LngLat;
    projects: MapProject[];
  }>(null);
  const selectedProjectIds = useMemo(
    () => popup?.projects.map((project) => project.id) ?? [],
    [popup]
  );
  const [filterMap, setFilterMap] = useState(false);
  const [extent, setExtent] = useState<Extent | null>(null);
  const mapRefBounds = map?.getBounds();
  const mapExtent =
    extent ?? (mapRefBounds ? convertLngLatBoundsToExtent(mapRefBounds) : null) ?? null;
  const projectsOnMap = useMemo(
    () =>
      projectList.filteredProjects.filter(
        (project): project is MapProject => project.location != null
      ),
    [projectList.filteredProjects]
  );
  const locationFilteredProjects = useMemo(
    () =>
      filterMap && mapExtent
        ? projectList.sortedProjects.filter((project): project is MapProject =>
            locationFilterProject(project, mapExtent)
          )
        : projectList.sortedProjects,
    [filterMap, mapExtent, projectList.sortedProjects]
  );
  const projectsOnPage = locationFilteredProjects.slice(
    (projectList.page - 1) * projectList.limit,
    projectList.page * projectList.limit
  );
  const numPages = Math.ceil(locationFilteredProjects.length / projectList.limit);
  const onMapMoveEnd = useCallback(
    (evt: ViewStateChangeEvent) => setExtent(convertLngLatBoundsToExtent(evt.target.getBounds())),
    []
  );
  const handleMapClick = useCallback(
    ([lng, lat]: [number, number]) => {
      const projects = projectsOnMap.filter(
        (project) =>
          lng === project.location.coordinates[0] && lat === project.location.coordinates[1]
      );
      if (projects.length > 0) {
        setTimeout(() => {
          map?.flyTo({ center: [lng, lat], speed: 0.2 });
          setPopup({ point: new MapLibreGL.LngLat(lng, lat), projects });
        });
      }
    },
    [map, projectsOnMap]
  );
  const closePopup = () => setPopup(null);
  const bounds = useProjectListMapBounds({ projectsOnMap, mapId });

  return {
    ...projectList,
    projectsOnPage,
    numPages,
    projectsOnMap,
    onMapMoveEnd,
    handleMapClick,
    hoveredProjectId,
    setHoveredProjectId,
    selectedProjectIds,
    popup,
    closePopup,
    filterMap,
    setFilterMap,
    bounds,
  };
}
